// Price lists — data access for Sales → Price lists (PX-SAL-320, ADR-049)
// and Procurement → Purchase price lists (PX-PUR-320, ADR-051). One shape,
// two kinds: the purchase RPCs are the sales ones with "purchase_" before
// "price_list" (purchase lists name their supplier; sales lists have
// customers and a company default).
//
//   price_list_page(company, status, term, after code, limit)   keyset on code
//                                     (price_lists_code_key), ~10 columns per row
//   price_list_document(id)           header + customers, one round-trip
//   price_list_line_page(id, term, after, limit)  keyset (item, unit, break)
//   save_price_list / price_list_action / save_price_list_lines /
//   adjust_price_list / assign_price_list_customer
// Types only from @patronix/domain (the loader path stays free of zod).

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { PriceListStatus } from "@patronix/domain/price-lists";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export type PriceListKind = "sales" | "purchase";
/** The RPC of a kind: price_list_page → purchase_price_list_page. */
const rpc = (kind: PriceListKind, name: string) => (kind === "purchase" ? name.replace("price_list", "purchase_price_list") : name);
export const priceListPermission = (kind: PriceListKind) => (kind === "purchase" ? "procurement.price.manage" : "sales.price.manage");

export type PriceListFilter = "open" | "all" | PriceListStatus;
export const priceListFilters: PriceListFilter[] = ["open", "draft", "active", "closed", "all"];
export const isPriceListFilter = (v: unknown): v is PriceListFilter => typeof v === "string" && (priceListFilters as string[]).includes(v);

export interface PriceListRow {
  id: string;
  code: string;
  name: string;
  currency: string;
  valid_from: string;
  valid_to: string | null;
  status: PriceListStatus;
  line_count: number;
  /** Sales lists. */
  is_company_default?: boolean;
  customer_count?: number;
  /** Purchase lists. */
  supplier_code?: string;
  supplier_name?: string;
}

export interface PriceListDocument {
  id: string;
  company_id: string;
  code: string;
  name: string;
  currency: string;
  valid_from: string;
  valid_to: string | null;
  status: PriceListStatus;
  line_count: number;
  revision: number;
  updated_at: string;
  /** Sales lists. */
  is_company_default?: boolean;
  customers?: { customer_id: string; code: string; name: string }[];
  /** Purchase lists. */
  supplier_id?: string;
  supplier_code?: string;
  supplier_name?: string;
}

export interface PriceListLine {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  min_qty: string | number;
  unit_price: string | number;
  cursor: { i: string; u: string; q: string | number };
}

export interface PriceListHeaderInput {
  code?: string;
  name?: string;
  currency?: string;
  valid_from?: string;
  valid_to?: string | null;
  /** Sales lists. */
  is_company_default?: boolean;
  /** Purchase lists: on creation only. */
  supplier_id?: string;
}

export interface PriceListLineUpsert {
  item_id: string;
  uom_id?: string;
  min_qty?: string;
  unit_price: string;
}

export interface ListParams {
  kind: PriceListKind;
  companyId: string;
  status: PriceListFilter;
  q: string;
}

export const PL_PAGE = 50;
const STALE = 60_000;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export const priceListKeys = {
  all: ["price-lists"] as const,
  list: (p: ListParams) => [...priceListKeys.all, "list", p] as const,
  doc: (kind: PriceListKind, id: string) => [...priceListKeys.all, kind, "doc", id] as const,
  lines: (kind: PriceListKind, id: string, q: string) => [...priceListKeys.all, kind, "lines", id, q] as const,
};

export async function listPriceLists(p: ListParams, cursor: string | null, signal: AbortSignal, size = PL_PAGE): Promise<Page<PriceListRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc(rpc(p.kind, "price_list_page"), { p_company_id: p.companyId, p_status: p.status, p_term: p.q.trim() || null, p_after: cursor, p_limit: size + 1 })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as PriceListRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  return { rows: page, nextCursor: hasMore ? (page[page.length - 1]?.code ?? null) : null };
}

export function priceListListQuery(p: ListParams) {
  return infiniteQueryOptions({
    queryKey: priceListKeys.list(p),
    queryFn: ({ pageParam, signal }) => listPriceLists(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<PriceListRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!p.companyId,
  });
}

export function priceListDocumentQuery(kind: PriceListKind, id: string) {
  return queryOptions({
    queryKey: priceListKeys.doc(kind, id),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc(rpc(kind, "price_list_document"), { p_id: id }).abortSignal(signal);
      if (error) throw error;
      return data as PriceListDocument | null;
    },
    staleTime: STALE,
  });
}

export async function listPriceListLines(
  kind: PriceListKind,
  id: string,
  q: string,
  cursor: string | null,
  signal: AbortSignal,
  size = 100,
): Promise<Page<PriceListLine>> {
  const { data, error } = await db()
    .rpc(rpc(kind, "price_list_line_page"), { p_id: id, p_term: q.trim() || null, p_after: cursor ? JSON.parse(cursor) : null, p_limit: size + 1 })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as PriceListLine[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.cursor) : null };
}

export function priceListLinesQuery(kind: PriceListKind, id: string, q: string) {
  return infiniteQueryOptions({
    queryKey: priceListKeys.lines(kind, id, q),
    queryFn: ({ pageParam, signal }) => listPriceListLines(kind, id, q, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<PriceListLine>) => last.nextCursor,
    staleTime: STALE,
  });
}

export async function savePriceList(kind: PriceListKind, id: string | null, companyId: string, header: PriceListHeaderInput, revision: number | null) {
  const { data, error } = await db().rpc(rpc(kind, "save_price_list"), {
    p_id: id,
    p_company_id: companyId,
    p_header: header,
    p_expected_revision: revision,
  });
  if (error) throw error;
  return data as string;
}

export async function priceListAction(kind: PriceListKind, id: string, action: "activate" | "close", revision: number | null) {
  const { error } = await db().rpc(rpc(kind, "price_list_action"), { p_id: id, p_action: action, p_expected_revision: revision });
  if (error) throw error;
}

export async function savePriceListLines(kind: PriceListKind, id: string, upserts: PriceListLineUpsert[], deletes: string[], revision: number | null) {
  const { error } = await db().rpc(rpc(kind, "save_price_list_lines"), {
    p_id: id,
    p_upserts: upserts,
    p_deletes: deletes,
    p_expected_revision: revision,
  });
  if (error) throw error;
}

export async function adjustPriceList(kind: PriceListKind, id: string, pct: number, decimals: number, revision: number | null) {
  const { data, error } = await db().rpc(rpc(kind, "adjust_price_list"), { p_id: id, p_pct: pct, p_decimals: decimals, p_expected_revision: revision });
  if (error) throw error;
  return data as { changed: number };
}

export async function assignPriceListCustomer(priceListId: string, customerId: string, assign: boolean) {
  const { error } = await db().rpc("assign_price_list_customer", { p_price_list_id: priceListId, p_customer_id: customerId, p_assign: assign });
  if (error) throw error;
}

export function priceListErrorMessage(e: unknown, fallback: string, stale: string): string {
  const err = e as { message?: string; code?: string };
  if (err.code === "PT409") return stale;
  return err.message || fallback;
}
